 
import { action } from 'mobx';
import Hls from 'hls.js';
import type {
  ManifestParsedData,
  ManifestLoadedData,
  FragLoadedData,
  FragChangedData,
  BufferAppendedData,
  BufferCreatedData,
  FragParsingInitSegmentData,
  AudioTracksUpdatedData,
  AudioTrackSwitchingData,
  AudioTrackSwitchedData,
  NonNativeTextTracksData,
  CuesParsedData,
  ErrorData,
  InitPTSFoundData,
} from 'hls.js';

import type { PlayerAdapterState } from './playerAdapterState';
import { HlsManifestStatus } from './playerAdapterSchema';
import { setCookies, assertNotNull } from '../../core';
import { JobModel, type MediaInfo } from '../../entities';
import { VIDEO_HLS_EVENTS } from './hls/hlsEvents';

const HLS_LOGS = false;
const HLS_VERBOSE_LOGS = false;

/** The hls events for which the adapter will listen. */
const VIDEO_HLS_LISTENER_EVENTS = VIDEO_HLS_EVENTS;

// Re-export Hls static members for use in this module.
// This avoids `isolatedModules` treating the default import as type-only.
const HlsEvents = Hls.Events;
const HlsErrorDetails = Hls.ErrorDetails;

export function initHlsPlayer(
  adapter: PlayerAdapterState,
  media: MediaInfo,
  video: HTMLVideoElement,
  isLiveStream: boolean,
  videoSrc: string
): Hls | null {
  if (!Hls.isSupported())
    return null;

  if (adapter.hls)
    return null;

  const hls = new Hls({
    renderTextTracksNatively: false
  });

  hls.config.xhrSetup = (xhr, _url) => {
    xhr.withCredentials = !media.isPublic && !isLiveStream;
  }
  hls.config.startLevel = -1;

  if (isLiveStream) {
    hls.config.liveSyncDuration = 3;
    hls.config.liveBackBufferLength = 1200;
    hls.config.maxBufferSize = 220 * 1000 * 1000;
  }

  hls.attachMedia(video);
  hls.loadSource(videoSrc);

  VIDEO_HLS_LISTENER_EVENTS.forEach((evtName) =>
    hls.on(evtName, (evt: string, data: unknown) => dispatchHlsEvent(adapter, evt, data)));

  return hls;
}

export function destroyHls(adapter: PlayerAdapterState): void {
  if (adapter.hls) {
    adapter.hls.stopLoad();
    adapter.hls.detachMedia();
    adapter.hls.destroy();
    adapter.hls = null;
  }
}

export const handleHlsManifestParsed = action((
  adapter: PlayerAdapterState,
  _evtType: string,
  data: ManifestParsedData) => {
  adapter.syncStateFromDom();
  adapter.hlsManifestStatus = HlsManifestStatus.Parsed;
  adapter.timer && clearTimeout(adapter.timer);
  HLS_LOGS && console.log('manifest parsed', data);
});

export const handleHlsManifestLoaded = action((
  adapter: PlayerAdapterState,
  _evtType: string,
  data: ManifestLoadedData) => {
  adapter.hlsManifestStatus = HlsManifestStatus.Loaded;
  adapter.timer && clearTimeout(adapter.timer);
  HLS_LOGS && console.log('manifest loaded', data);
});

export const handleHlsFragmentLoaded = action((
  adapter: PlayerAdapterState,
  _evtType: string,
  data: FragLoadedData) => {
  adapter.fragments.push(data.frag);
});

export const handleHlsFragChanged = action((
  adapter: PlayerAdapterState,
  _evtType: string,
  data: FragChangedData) => {
  adapter.currentFragment = data.frag;
  if (!adapter.initFragment) {
    adapter.initFragment = data.frag;
    HLS_LOGS && console.log('init frag', data);
  }
});

export const handleHlsBufferAppended = action((
  _adapter: PlayerAdapterState,
  _evtType: string,
  _data: BufferAppendedData) => { /* noop */ });

export const handleHlsBufferCreated = action((
  adapter: PlayerAdapterState,
  _evtType: string,
  data: BufferCreatedData) => {
  HLS_LOGS && console.log('buffer created', data);

  if (!data || !adapter.isLiveStream)
    return;

  const tracks = data.tracks;

  if (!tracks.video) {
    if (adapter.autoplay)
      adapter.autoplayAfterRetry = true;

    console.warn('NO VIDEO TRACK AVAILABLE');

    adapter.reattachDOMVideo();
  } else if (adapter.autoplayAfterRetry) {
    adapter.invoke('setAutoplay', { autoplay: true });
    adapter.autoplayAfterRetry = false;
  }
});

export const handleHlsInitSegmentParsed = action((
  adapter: PlayerAdapterState,
  _evtType: string,
  _data: FragParsingInitSegmentData) => {

  if (!_data)
    return;

  adapter.fragmentDuration = adapter.currentFragment?.duration || null;
  HLS_LOGS && console.log('first segment parsed', _data);
});

export const handleHlsBufferEOS = action((
  adapter: PlayerAdapterState,
  _evtType: string) => {
  adapter.emit('endOfStream');
  HLS_LOGS && console.log('EOS');
});

export const handleHlsAudioTracksUpdated = action((
  adapter: PlayerAdapterState,
  _evtType: string,
  data: AudioTracksUpdatedData) => {
  HLS_LOGS && console.log('AUDIO TRACKS UPDATED', data, adapter.hls);
  adapter.playerAudioTracks.refreshAudioTracks();
});

export const handleHlsAudioTrackSwitching = action((
  adapter: PlayerAdapterState,
  _evtType: string,
  data: AudioTrackSwitchingData) => {
  HLS_LOGS && console.log('AUDIO TRACK SWITCHING', data);
  const trackId = data.id?.toString() ?? null;
  adapter.playerAudioTracks.setSelectedTrack(trackId);
});

export const handleHlsAudioTrackSwitched = action((
  adapter: PlayerAdapterState,
  _evtType: string,
  data: AudioTrackSwitchedData) => {
  HLS_LOGS && console.log('AUDIO TRACK SWITCHED', data);
  adapter.playerAudioTracks.refreshActiveAudioTrack();
  HLS_LOGS && console.log('ACTIVE AUDIO TRACK', adapter.playerAudioTracks.activeAudioTrack);
});

export const handleHlsAudioTrackError = action(
  (adapter: PlayerAdapterState, data: ErrorData) => {
    HLS_LOGS && console.log('AUDIO TRACK ERROR', data);
    adapter.playerAudioTracks.setError();
    adapter.playerAudioTracks.refreshActiveAudioTrack();
  });

export const handleHlsInitPTSFound = action((
  adapter: PlayerAdapterState,
  _evtType: string,
  data: InitPTSFoundData) => {
  adapter.initPTS = data.initPTS;
  HLS_LOGS && console.log('init pts', data);
});

export const handleHlsNonNativeTextTracksFound = action((
  adapter: PlayerAdapterState,
  _evtType: string,
  data: NonNativeTextTracksData) => {
  const tracks = data.tracks;

  HLS_LOGS && console.log('hlsNonNativeTextTracksFound', data);

  tracks.forEach((track) => {
    const textTrack: HTMLTrackElement & { isCaption?: boolean } = document.createElement('track');
    textTrack.kind = 'metadata';
    textTrack.isCaption = true;
    textTrack.label = track.label;
    textTrack.default = track.default;

    if (track.kind === 'captions') {
      textTrack.id = track._id ?? '';
      textTrack.srclang = track.label;
    } else if (track.kind === 'subtitles') {
      const subtitleTrack = (track as unknown as { subtitleTrack?: { id?: string; lang?: string } }).subtitleTrack;
      textTrack.id = subtitleTrack?.id ?? '';
      textTrack.srclang = subtitleTrack?.lang ?? '';
    }

    adapter.videoElement?.appendChild(textTrack);
    textTrack.track.mode = 'hidden';
  })
});

export const handleHlsCuesParsed = action((
  adapter: PlayerAdapterState,
  _evtType: string,
  data: CuesParsedData) => {

  HLS_LOGS && console.log('hlsCuesParsed', data, adapter.hls);

  const type = data.type;
  const cues = data.cues;
  const track = data.track;
  const video = adapter.videoElement;
  const textTracks = video?.textTracks ?? [];

  let trackId: string | null = null;

  if (type === 'subtitles') {
    trackId = (track === 'default') ? 'default' : track.substring(type.length);
  } else if (type === 'captions') {
    trackId = track;
  }

  if (trackId === 'default') {
    const subtitleTracks = adapter.hls!.subtitleTracks || [];
    trackId = subtitleTracks.find((subtitle) => !!subtitle.default)?.id.toString() ?? adapter.hls!.subtitleTrack.toString();
  }

  const textTrack = [...Object.values(textTracks)].find(item => item.id === trackId && item.mode !== 'disabled') ?? null;

  if (textTrack) {
    cues.forEach((cue: VTTCue) => {
      if (!cue.id)
        textTrack!.addCue(cue);
      else if (!textTrack!.cues?.getCueById(cue.id)) {
        try {
          textTrack!.addCue(cue);
          if (!textTrack!.cues?.getCueById(cue.id)) {
            throw new Error(`addCue is failed for: ${cue}`);
          }
        } catch (_err) {
          const textTrackCue = new VTTCue(cue.startTime, cue.endTime, cue.text);
          textTrackCue.id = cue.id;
          textTrack!.addCue(textTrackCue);
        }
      }
    });
  }
});

export const handleHlsError = action((
  adapter: PlayerAdapterState,
  _evtType: string,
  data: ErrorData) => {
  HLS_LOGS && console.log('HLS Error', data);

  switch (data.details) {
    case HlsErrorDetails.MANIFEST_LOAD_ERROR:
    case HlsErrorDetails.MANIFEST_LOAD_TIMEOUT:
    case HlsErrorDetails.MANIFEST_PARSING_ERROR:
      handleHlsManifestError(adapter, data);
      break;
    case HlsErrorDetails.FRAG_LOAD_ERROR:
      handleHlsFragmentLoadError(adapter, data);
      break;
    case HlsErrorDetails.AUDIO_TRACK_LOAD_ERROR:
    case HlsErrorDetails.AUDIO_TRACK_LOAD_TIMEOUT:
      handleHlsAudioTrackError(adapter, data);
      break;
    default:
      break;
  }
});

export const handleHlsFragmentLoadError = action((
  adapter: PlayerAdapterState,
  _data: ErrorData) => {
  adapter.emit('sourceError');
  reauthorize(adapter);
});

export const handleHlsManifestError = action((
  adapter: PlayerAdapterState,
  data: ErrorData) => {
  adapter.hlsManifestStatus = HlsManifestStatus.Error;
  if (data.response?.code === 404) {
    adapter.timer = setTimeout(() => adapter.reattachDOMVideo(), 3000)
  }
});

export async function reauthorize(adapter: PlayerAdapterState): Promise<void> {
  const { hls, store } = adapter;
  const video = adapter.videoElement;
  const position = video?.currentTime ?? adapter.currentTime;
  const resume = !!video && !video.paused;

  if (hls)
    hls.stopLoad();

  const [jobRes, err] = await store.api.getJob({ id: adapter.jobId });
  if (err)
    return;
  assertNotNull(jobRes);

  const { media } = new JobModel(jobRes.getJob!, store);
  const cookies = media.hls?.cookieCredentials;
  if (!media || !cookies)
    return;

  setCookies(media.hls.cookieCredentials);

  if (!hls)
    return;
  // Segment URLs carry the manifest's signature, which CloudFront prefers over cookies; agency
  // media is signed for minutes, so reload the freshly signed manifest rather than only cookies.
  if (!media.hls.signedUrl) {
    hls.startLoad(position);
    return;
  }
  // A new source restarts loading at startPosition once the manifest is parsed; keep the viewer's place.
  hls.config.startPosition = position;
  if (resume)
    hls.once(Hls.Events.MANIFEST_PARSED, () => void video?.play());
  hls.loadSource(media.hls.signedUrl);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- HLS.js event union is complex; using string + any for the dispatcher is simpler than a full discriminated union
export function dispatchHlsEvent(adapter: PlayerAdapterState, evt: string, data: any): void {
  switch (evt) {
    case HlsEvents.MANIFEST_PARSED:
      handleHlsManifestParsed(adapter, evt, data);
      break;
    case HlsEvents.MANIFEST_LOADED:
      handleHlsManifestLoaded(adapter, evt, data);
      break;
    case HlsEvents.ERROR:
      handleHlsError(adapter, evt, data);
      break;
    case HlsEvents.INIT_PTS_FOUND:
      handleHlsInitPTSFound(adapter, evt, data);
      break;
    case HlsEvents.FRAG_PARSING_INIT_SEGMENT:
      handleHlsInitSegmentParsed(adapter, evt, data)
      break;
    case HlsEvents.FRAG_CHANGED:
      handleHlsFragChanged(adapter, evt, data);
      break;
    case HlsEvents.BUFFER_EOS:
      handleHlsBufferEOS(adapter, evt);
      break;
    case HlsEvents.BUFFER_CREATED:
      handleHlsBufferCreated(adapter, evt, data);
      break;
    case HlsEvents.BUFFER_APPENDED:
      handleHlsBufferAppended(adapter, evt, data);
      break;
    case HlsEvents.AUDIO_TRACKS_UPDATED:
      handleHlsAudioTracksUpdated(adapter, evt, data)
      break;
    case HlsEvents.AUDIO_TRACK_SWITCHING:
      handleHlsAudioTrackSwitching(adapter, evt, data)
      break;
    case HlsEvents.AUDIO_TRACK_SWITCHED:
      handleHlsAudioTrackSwitched(adapter, evt, data)
      break;
    case HlsEvents.NON_NATIVE_TEXT_TRACKS_FOUND:
      handleHlsNonNativeTextTracksFound(adapter, evt, data)
      break;
    case HlsEvents.CUES_PARSED:
      handleHlsCuesParsed(adapter, evt, data)
      break;
    default:
      adapter.emit('HLS_' + evt);
      break;
  }

  HLS_VERBOSE_LOGS
    && console.log(evt, data, adapter.hls);
}
